import { ApiError } from './api/client.js'

/**
 * A demo is built from hundreds or thousands of calls, many in flight at
 * once, and a few of them will meet a throttled Lambda, a cold start that
 * ran long or a dropped connection. Those answers come from the gateway,
 * not the API - a bare 429, 500, 502, 503 or 504, or no CORS headers at
 * all, which the browser reports as a network failure - and every call
 * the demo makes is safe to repeat (imports upsert, events carry
 * idempotency keys, setup calls set what they set), so each is tried
 * again a few times, waiting a little longer each time, before the demo
 * gives up on it. A refusal the API itself gave (a 4xx other than 429)
 * is not retried: it would only be refused again.
 */
const ATTEMPTS = 5
const FIRST_WAIT_MS = 500

export function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 429 || error.status >= 500
  }
  // fetch rejects with a TypeError for a network failure, which is also what a response without CORS headers looks like from here.
  return error instanceof TypeError
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function withRetry<T>(call: () => Promise<T>, attempts: number = ATTEMPTS): Promise<T> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await call()
    } catch (error: unknown) {
      if (attempt >= attempts || !isRetryable(error)) {
        throw error
      }
      // 0.5 s, 1 s, 2 s, 4 s, each with a little jitter so retries from parallel calls do not land together.
      await wait(FIRST_WAIT_MS * 2 ** (attempt - 1) * (0.75 + Math.random() * 0.5))
    }
  }
}
